/**
 * Settings Storage Module
 * Manages persistent storage of user configuration including authorization cookies
 */

export interface Settings {
    authorizationKey?: string;
    version: number;
    lastUpdated: number;
}

export interface SettingsStorage {
    getAuthorizationKey(): string | null;
    setAuthorizationKey(key: string): void;
    removeAuthorizationKey(): void;
    isAvailable(): boolean;
    getSettings(): Settings | null;
    setSettings(settings: Settings): void;
}

/**
 * Storage types for fallback strategy
 */
type StorageType = 'localStorage' | 'sessionStorage' | 'memory';

/**
 * In-memory storage fallback for when browser storage is unavailable
 */
class MemoryStorage {
    private data: Record<string, string> = {};

    getItem(key: string): string | null {
        return this.data[key] || null;
    }

    setItem(key: string, value: string): void {
        this.data[key] = value;
    }

    removeItem(key: string): void {
        delete this.data[key];
    }
}

/**
 * Settings storage implementation with fallback strategies
 */
class SettingsStorageImpl implements SettingsStorage {
    private static readonly SETTINGS_KEY = 'veo-banana-settings';
    private static readonly CURRENT_VERSION = 1;

    private storage!: Storage | MemoryStorage;
    private storageType!: StorageType;
    private memoryStorage = new MemoryStorage();

    constructor() {
        // Initialize with fallback strategy
        this.initializeStorage();
    }

    /**
     * Initialize storage with fallback strategy:
     * 1. Try localStorage (primary)
     * 2. Fall back to sessionStorage (session-only)
     * 3. Fall back to in-memory storage (no persistence)
     */
    private initializeStorage(): void {
        // Try localStorage first
        if (this.testStorage(window.localStorage)) {
            this.storage = window.localStorage;
            this.storageType = 'localStorage';
            return;
        }

        // Fall back to sessionStorage
        if (this.testStorage(window.sessionStorage)) {
            this.storage = window.sessionStorage;
            this.storageType = 'sessionStorage';
            return;
        }

        // Fall back to memory storage
        this.storage = this.memoryStorage;
        this.storageType = 'memory';
    }

    /**
     * Test if a storage mechanism is available and working
     */
    private testStorage(storage: Storage): boolean {
        try {
            const testKey = '__veo_banana_storage_test__';
            storage.setItem(testKey, 'test');
            storage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Check if persistent storage is available
     */
    isAvailable(): boolean {
        return this.storageType === 'localStorage';
    }

    /**
     * Get the current storage type being used
     */
    getStorageType(): StorageType {
        return this.storageType;
    }

    /**
     * Get all settings from storage
     */
    getSettings(): Settings | null {
        try {
            const settingsJson = this.storage.getItem(SettingsStorageImpl.SETTINGS_KEY);
            if (!settingsJson) {
                return null;
            }

            const settings = JSON.parse(settingsJson) as Settings;

            // Validate settings structure
            if (typeof settings !== 'object' || settings === null) {
                return null;
            }

            // Handle version migration if needed
            if (settings.version !== SettingsStorageImpl.CURRENT_VERSION) {
                return this.migrateSettings(settings);
            }

            return settings;
        } catch (error) {
            console.warn('Failed to parse settings from storage:', error);
            return null;
        }
    }

    /**
     * Save settings to storage
     */
    setSettings(settings: Settings): void {
        try {
            const settingsToSave: Settings = {
                ...settings,
                version: SettingsStorageImpl.CURRENT_VERSION,
                lastUpdated: Date.now(),
            };

            const settingsJson = JSON.stringify(settingsToSave);
            this.storage.setItem(SettingsStorageImpl.SETTINGS_KEY, settingsJson);
        } catch (error) {
            console.error('Failed to save settings to storage:', error);
            throw new Error('Failed to save settings');
        }
    }

    /**
     * Get authorization cookie from settings
     */
    getAuthorizationKey(): string | null {
        const settings = this.getSettings();
        return settings?.authorizationKey || null;
    }

    /**
     * Set authorization cookie in settings
     */
    setAuthorizationKey(key: string): void {
        // Validate cookie format before saving
        if (!this.isValidAuthorizationKey(key)) {
            throw new Error('Invalid authorization cookie format');
        }

        const currentSettings = this.getSettings() || {
            version: SettingsStorageImpl.CURRENT_VERSION,
            lastUpdated: Date.now(),
        };

        const updatedSettings: Settings = {
            ...currentSettings,
            authorizationKey: key,
        };

        this.setSettings(updatedSettings);
    }

    /**
     * Remove authorization cookie from settings
     */
    removeAuthorizationKey(): void {
        const currentSettings = this.getSettings();
        if (!currentSettings) {
            return; // Nothing to remove
        }

        const updatedSettings: Settings = {
            ...currentSettings,
            authorizationKey: undefined,
        };

        this.setSettings(updatedSettings);
    }

    /**
     * Validate authorization key format
     * Rules:
     * - Must be a valid cookie string format
     * - Minimum length: 10 characters
     * - Maximum length: 4096 characters (standard cookie limit)
     * - Can contain various cookie characters including =, ;, spaces, etc.
     */
    private isValidAuthorizationKey(key: string): boolean {
        if (typeof key !== 'string') {
            return false;
        }

        // Check length (cookies can be quite long)
        if (key.length < 10 || key.length > 4096) {
            return false;
        }

        // Basic length check
        if (key.length < 1) {
            return false;
        }

        return true;
    }

    /**
     * Migrate settings from older versions
     */
    private migrateSettings(settings: any): Settings | null {
        try {
            // For now, just reset to current version if migration is needed
            // In the future, this could handle more complex migrations
            const migratedSettings: Settings = {
                version: SettingsStorageImpl.CURRENT_VERSION,
                lastUpdated: Date.now(),
            };

            // Preserve authorization cookie if it exists and is valid
            if (settings.authorizationKey && this.isValidAuthorizationKey(settings.authorizationKey)) {
                migratedSettings.authorizationKey = settings.authorizationKey;
            }

            // Save migrated settings
            this.setSettings(migratedSettings);
            return migratedSettings;
        } catch (error) {
            console.warn('Failed to migrate settings:', error);
            return null;
        }
    }

    /**
     * Clear all settings (useful for testing or reset functionality)
     */
    clearSettings(): void {
        try {
            this.storage.removeItem(SettingsStorageImpl.SETTINGS_KEY);
        } catch (error) {
            console.error('Failed to clear settings:', error);
        }
    }
}

// Export singleton instance
export const settingsStorage: SettingsStorage = new SettingsStorageImpl();

// Export validation function for external use
export function isValidAuthorizationKey(key: string): boolean {
    if (typeof key !== 'string') {
        return false;
    }

    // Check length (cookies can be quite long)
    if (key.length < 10 || key.length > 4096) {
        return false;
    }

    // Basic length check
    if (key.length < 1) {
        return false;
    }

    // We allow any string now as it might be a raw token or a cookie string
    return true;
}

// Export storage type for external use
export type { StorageType };
